import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { log } from '@/logger/logger.ts';
import { I18N_KEYS } from '@/language/keys';
import { useGetNodes, useModelBaseView, useNodeData } from '@/components/Recipe/FlowContext';
import { useBatchesByNodeId, useGetActiveBatchesWithActiveNode } from '@/components/Recipe/RunFlow/batches.store';
import { useFlowRunContext } from '@/components/Recipe/RunFlow/FlowRunContext';
import { getNodeRunsInBatches, areAllRunsFailedOrCompleted } from '@/components/Recipe/RunFlow/batch.utils';
import { MODEL_ERROR_MESSAGE_OVERRIDE } from './consts/model-error-message-override.const';
import type { AiModelData } from '@/types/nodes/model';
import type { BatchRun, NodeRun, NodeRunStatus, RecipeRun } from '@/types/batch.types';
import type { RunModelButtonStatus } from './RunModelButton';
import type { UploadedAsset } from '@/types/api/assets';

const logger = log.getLogger('useRunModel');

interface UseRunModelOptions {
  id: string;
  setErrorMessage: (error: string | object | null) => void;
}

export const useRunModel = ({ id, setErrorMessage }: UseRunModelOptions) => {
  const modelBaseView = useModelBaseView(id);
  const nodeData = useNodeData<AiModelData>(id);
  const modelName = nodeData.model?.name;
  const { runNode, addNodeCallback, stopBatch, getBatchCost, initializingBatchNodes } = useFlowRunContext();
  const getNodes = useGetNodes();
  const { t } = useTranslation();
  const [status, setStatus] = useState<NodeRunStatus>();

  const onSuccess = useCallback(
    (nodeRun: NodeRun, recipeRun: RecipeRun) => {
      const { result: results, input, inputIndex: nodeInputIndex } = nodeRun;
      const { runIndexInBatch, batchId: batchId, inputIndex: recipeInputIndex } = recipeRun;
      // Use nodeRun.inputIndex for dynamic iterator expansion, fallback to recipeRun.inputIndex
      const secondaryOrder = nodeInputIndex ?? recipeInputIndex;
      if (results && results.length >= 1) {
        const incomingResults: UploadedAsset[] = results.map(
          (res) =>
            ({
              ...res,
              batchId,
              order: runIndexInBatch,
              secondaryOrder,
              input,
            }) as UploadedAsset,
        );
        modelBaseView.appendResult(incomingResults, runIndexInBatch, batchId, secondaryOrder);
      }
    },
    [modelBaseView],
  );

  const onError = useCallback(
    (error?: unknown) => {
      let message =
        typeof error === 'string'
          ? error
          : typeof error === 'object'
            ? error
              ? JSON.stringify(error)
              : null
            : t(I18N_KEYS.GENERAL.UNKNOWN_ERROR);

      if (typeof message === 'string' && modelName) {
        const overrides = MODEL_ERROR_MESSAGE_OVERRIDE[modelName];
        if (overrides) {
          for (const [pattern, replacement] of Object.entries(overrides)) {
            if (message.includes(pattern)) {
              message = message.replace(pattern, replacement);
              break;
            }
          }
        }
      }

      setErrorMessage(message);
    },
    [setErrorMessage, t, modelName],
  );

  const batches = useBatchesByNodeId(id);
  const activeBatchesWithActiveNode = useGetActiveBatchesWithActiveNode(id);

  const isBatchRunning = status === 'PENDING' || status === 'RUNNING';

  const cancelBatchRun = useCallback(async () => {
    if (isBatchRunning) {
      // only stop the batch if it is the only one running for the node, and it is the only node running in the batch
      const batchIds = batches
        .filter((batch) =>
          batch.recipeRuns.some(
            (recipeRun) =>
              recipeRun.nodeRuns.every((nodeRun) => nodeRun.nodeId === id) && recipeRun.status !== 'CANCELED',
          ),
        )
        .filter((batch) =>
          batch.recipeRuns.some((recipeRun) =>
            recipeRun.nodeRuns.some((nodeRun) => nodeRun.status === 'RUNNING' || nodeRun.status === 'PENDING'),
          ),
        )
        .map((batch) => batch.batchId);
      const distinctBatchIds = new Set(batchIds);
      if (distinctBatchIds.size === 0) return;

      const batchId = Array.from(distinctBatchIds)[0];
      await stopBatch(batchId);
      setStatus('CANCELED');
    }
  }, [isBatchRunning, batches, stopBatch, id]);

  const processedNodeRuns = useRef<NodeRun[]>([]);

  const onBatchUpdate = useCallback(
    (batch: BatchRun, recipeRun: RecipeRun, nodeRun: NodeRun) => {
      if (nodeRun.nodeId !== id) {
        return;
      }
      if (recipeRun.status === 'CANCELED') {
        setStatus(recipeRun.status);
        return;
      }

      let processedNodeRunIndex = processedNodeRuns.current.findIndex(
        (processedNodeRun) => processedNodeRun.id === nodeRun.id,
      );
      let isFirstUpdate = false;
      if (processedNodeRunIndex === -1) {
        processedNodeRuns.current.push(nodeRun);
        processedNodeRunIndex = processedNodeRuns.current.length - 1;
        isFirstUpdate = true;
      }

      const processedNodeRun = processedNodeRuns.current[processedNodeRunIndex];
      if (!processedNodeRun || processedNodeRun.status === 'CANCELED') {
        return;
      }

      const isAllRunsFailedOrCompleted = areAllRunsFailedOrCompleted(recipeRun);

      if (recipeRun.status === 'FAILED' && isAllRunsFailedOrCompleted) {
        processedNodeRuns.current[processedNodeRunIndex] = nodeRun;

        if (nodeRun.status === 'COMPLETED') {
          setStatus('COMPLETED');
          if (processedNodeRun.status === 'RUNNING' || processedNodeRun.status === 'PENDING') {
            onSuccess(nodeRun, recipeRun);
          }
        } else if (nodeRun.status === 'FAILED') {
          setStatus('FAILED');
          if (nodeRun.error) {
            onError(nodeRun.error);
          }
        }

        return;
      }

      switch (nodeRun.status) {
        case 'PENDING':
          setStatus('PENDING');
          break;
        case 'RUNNING':
          setStatus('RUNNING');
          if (processedNodeRun.status === 'PENDING' || isFirstUpdate) {
            processedNodeRuns.current[processedNodeRunIndex] = nodeRun;
          }
          break;
        case 'CANCELED':
          setStatus('CANCELED');
          processedNodeRuns.current[processedNodeRunIndex] = nodeRun;
          break;
        case 'COMPLETED':
          setStatus('COMPLETED');
          if (processedNodeRun.status === 'RUNNING' || processedNodeRun.status === 'PENDING' || isFirstUpdate) {
            onSuccess(nodeRun, recipeRun);
            processedNodeRuns.current[processedNodeRunIndex] = nodeRun;
          }
          break;
        case 'FAILED':
          if (processedNodeRun.status === 'RUNNING' || processedNodeRun.status === 'PENDING' || isFirstUpdate) {
            processedNodeRuns.current[processedNodeRunIndex] = nodeRun;
          }
          break;
        default:
          break;
      }
    },
    [id, onError, onSuccess],
  );

  useEffect(() => {
    return addNodeCallback(id, onBatchUpdate);
  }, [addNodeCallback, id, onBatchUpdate]);

  useEffect(() => {
    const isInitializing = Object.values(initializingBatchNodes).some((nodeIds) => nodeIds.includes(id));
    if (isInitializing) {
      onError(null);
    }
  }, [initializingBatchNodes, id, onError]);

  const progress = useMemo<string | undefined>(() => {
    if (activeBatchesWithActiveNode.length === 0) {
      return undefined;
    }
    if (activeBatchesWithActiveNode.length > 1) {
      logger.warn('progress: Active node appears in multiple batches. This should not happen', {
        id,
        activeBatchesWithActiveNode,
      });
      return undefined;
    }

    const nodeRunsInBatches = getNodeRunsInBatches(id, activeBatchesWithActiveNode);
    if (!nodeRunsInBatches?.length) {
      return undefined;
    }
    if (nodeRunsInBatches.length === 1) {
      return `${nodeRunsInBatches[0].progress || 0}%`;
    }
    return `${nodeRunsInBatches.filter((nodeRun) => nodeRun.status === 'COMPLETED').length} / ${nodeRunsInBatches.length}`;
  }, [id, activeBatchesWithActiveNode]);

  const run = useCallback(async () => {
    setStatus('PENDING');
    onError(null);
    try {
      const batchId = await runNode(id);
      if (!batchId) {
        setStatus(undefined);
      }
      return batchId;
    } catch (error: any) {
      setStatus(undefined);
      onError(error?.message || error);
      return null;
    }
  }, [runNode, id, onError]);

  const calculateCost = useCallback(
    async (numberOfRuns = 1) => {
      const node = getNodes().find((node) => node.id === id);
      if (!node) {
        return { cost: -1 };
      }
      return await getBatchCost([node], numberOfRuns);
    },
    [getBatchCost, id, getNodes],
  );

  const multiNodeBatch = useMemo(() => {
    if (activeBatchesWithActiveNode.length === 0) return false;
    if (activeBatchesWithActiveNode.length > 1) {
      logger.warn('multiNodeBatch: Active node appears in multiple batches. This should not happen', {
        id,
        activeBatchesWithActiveNode,
      });
      return true;
    }
    return activeBatchesWithActiveNode[0].recipeRuns.some((recipeRun) =>
      recipeRun.nodeRuns.some((nodeRun) => nodeRun.nodeId !== id),
    );
  }, [activeBatchesWithActiveNode, id]);

  return useMemo(() => {
    let buttonStatus: RunModelButtonStatus = 'initial';
    if (activeBatchesWithActiveNode.length > 0) {
      buttonStatus = status === 'PENDING' ? 'pending' : 'node-running';
    } else if (status === 'PENDING') {
      buttonStatus = 'pending';
    } else if (status === 'RUNNING') {
      buttonStatus = 'node-running';
    }
    return {
      cancelRun: cancelBatchRun,
      multiNodeBatch,
      progress,
      run,
      buttonStatus,
      calculateCost,
    };
  }, [status, activeBatchesWithActiveNode, cancelBatchRun, multiNodeBatch, progress, run, calculateCost]);
};
